import React, { useState, useEffect, useRef, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { X, ChevronLeft, ChevronRight, Trash2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

// Pin the pdfjs worker to a matching CDN build. react-pdf ships pdfjs-dist as
// a peer dep and needs a matching worker version at runtime. Using unpkg keeps
// us off any local webpack worker wiring.
pdfjs.GlobalWorkerOptions.workerSrc =
  `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const COLORS = [
  { key: "yellow", swatch: "bg-[#FFEB3B]", ring: "ring-[#FBC02D]", overlay: "rgba(255,235,59,0.38)" },
  { key: "green", swatch: "bg-[#66BB6A]", ring: "ring-[#2E7D32]", overlay: "rgba(102,187,106,0.38)" },
  { key: "pink", swatch: "bg-[#EC407A]", ring: "ring-[#AD1457]", overlay: "rgba(236,64,122,0.38)" },
];

const COLOR_MAP = Object.fromEntries(COLORS.map((c) => [c.key, c]));

const MIN_HL = 0.006; // ignore accidental micro-drags (0.6% of doc dim)

export default function DocumentViewerModal({ doc, onClose }) {
  const [highlights, setHighlights] = useState([]);
  const [pickedColor, setPickedColor] = useState("yellow");
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [dragRect, setDragRect] = useState(null); // {x,y,w,h} in normalized coords
  const [pageDims, setPageDims] = useState({ w: 0, h: 0 }); // rendered px dims of the current page
  const [pdfError, setPdfError] = useState(false);
  const [editing, setEditing] = useState(null); // highlight being edited via popover
  const [commentDraft, setCommentDraft] = useState("");
  const stageRef = useRef(null);
  const dragStart = useRef(null);

  const isImage = (doc?.mime_type || "").startsWith("image/");
  const isPdf =
    doc?.mime_type === "application/pdf" ||
    (doc?.original_name || "").toLowerCase().endsWith(".pdf");
  const supported = isImage || isPdf;

  const token = typeof window !== "undefined" ? localStorage.getItem("tt_token") : "";
  const fileUrl = doc
    ? `${API}/documents/${doc.id}/download?inline=1&token=${encodeURIComponent(token || "")}`
    : "";

  // Load existing highlights on mount.
  useEffect(() => {
    if (!doc?.id) return;
    (async () => {
      try {
        const { data } = await api.get(`/documents/${doc.id}/highlights`);
        setHighlights(data.highlights || []);
      } catch {
        // Non-fatal: viewer still renders without highlights.
      }
    })();
  }, [doc?.id]);

  // Reset per-doc state when a different doc is opened.
  useEffect(() => {
    setPageNum(1);
    setNumPages(1);
    setEditing(null);
    setDragRect(null);
    setPdfError(false);
  }, [doc?.id]);

  // Close on ESC.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const currentPageHighlights = highlights.filter((h) => (h.page || 1) === pageNum);

  // ---------- Drag-to-create ----------
  const onMouseDown = (e) => {
    if (!stageRef.current || !pageDims.w) return;
    // Ignore drags that start on an existing highlight (they open the editor).
    if (e.target?.dataset?.highlight === "existing") return;
    const rect = stageRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    dragStart.current = { x, y };
    setEditing(null);
    setDragRect({ x, y, w: 0, h: 0 });
  };

  const onMouseMove = (e) => {
    if (!dragStart.current || !stageRef.current) return;
    const rect = stageRef.current.getBoundingClientRect();
    const cx = (e.clientX - rect.left) / rect.width;
    const cy = (e.clientY - rect.top) / rect.height;
    const sx = dragStart.current.x;
    const sy = dragStart.current.y;
    const nx = Math.max(0, Math.min(1, Math.min(sx, cx)));
    const ny = Math.max(0, Math.min(1, Math.min(sy, cy)));
    const nw = Math.max(0, Math.min(1, Math.abs(cx - sx)));
    const nh = Math.max(0, Math.min(1, Math.abs(cy - sy)));
    setDragRect({ x: nx, y: ny, w: nw, h: nh });
  };

  const onMouseUp = async () => {
    if (!dragStart.current || !dragRect) {
      dragStart.current = null;
      return;
    }
    const r = dragRect;
    dragStart.current = null;
    setDragRect(null);
    if (r.w < MIN_HL || r.h < MIN_HL) return;
    try {
      const { data } = await api.post(`/documents/${doc.id}/highlights`, {
        color: pickedColor,
        x: r.x,
        y: r.y,
        w: r.w,
        h: r.h,
        page: pageNum,
        comment: null,
      });
      setHighlights((prev) => [...prev, data]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save highlight.");
    }
  };

  // ---------- Edit / delete ----------
  const openHighlight = (h) => {
    setEditing(h);
    setCommentDraft(h.comment || "");
  };

  const saveEditing = async (patch) => {
    if (!editing) return;
    try {
      const { data } = await api.patch(
        `/documents/${doc.id}/highlights/${editing.id}`,
        patch,
      );
      setHighlights((prev) => prev.map((h) => (h.id === data.id ? data : h)));
      setEditing(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update highlight.");
    }
  };

  const deleteHighlight = async () => {
    if (!editing) return;
    const id = editing.id;
    try {
      await api.delete(`/documents/${doc.id}/highlights/${id}`);
      setHighlights((prev) => prev.filter((h) => h.id !== id));
      setEditing(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete highlight.");
    }
  };

  if (!doc) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-stretch justify-center overflow-hidden"
      data-testid="doc-viewer-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="bg-[#FAF9F6] w-full h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Toolbar */}
        <div className="border-b border-[#E5E4DE] bg-white px-4 sm:px-6 py-3 flex items-center gap-3 sm:gap-4 flex-wrap">
          <div className="font-serif text-base sm:text-lg text-[#0D2B52] truncate flex-1 min-w-0" data-testid="viewer-title">
            {doc.title || doc.original_name}
          </div>

          {/* Color picker */}
          <div className="flex items-center gap-2" data-testid="viewer-color-picker">
            <span className="label-mono text-[10px] text-[#6B7280] hidden sm:inline">Highlight</span>
            {COLORS.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setPickedColor(c.key)}
                className={`w-6 h-6 rounded-full ${c.swatch} border border-[#E5E4DE] ring-offset-2 transition-all ${
                  pickedColor === c.key ? `ring-2 ${c.ring}` : ""
                }`}
                aria-label={`Use ${c.key} highlight`}
                data-testid={`viewer-color-${c.key}`}
                title={c.key}
              />
            ))}
          </div>

          {/* PDF page nav */}
          {isPdf && !pdfError && (
            <div className="flex items-center gap-1 border border-[#E5E4DE] bg-[#FAF9F6] px-2 py-1" data-testid="viewer-page-nav">
              <button
                type="button"
                onClick={() => setPageNum((p) => Math.max(1, p - 1))}
                disabled={pageNum <= 1}
                className="p-1 disabled:opacity-30"
                data-testid="viewer-page-prev"
                aria-label="Previous page"
              >
                <ChevronLeft size={16} strokeWidth={1.75} />
              </button>
              <span className="font-mono text-xs text-[#0D2B52] min-w-[3.5em] text-center">
                {pageNum} / {numPages || "?"}
              </span>
              <button
                type="button"
                onClick={() => setPageNum((p) => Math.min(numPages || p, p + 1))}
                disabled={pageNum >= (numPages || 1)}
                className="p-1 disabled:opacity-30"
                data-testid="viewer-page-next"
                aria-label="Next page"
              >
                <ChevronRight size={16} strokeWidth={1.75} />
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={onClose}
            className="btn-ghost inline-flex items-center gap-1.5 text-sm"
            data-testid="viewer-close"
            aria-label="Close viewer"
          >
            <X size={16} strokeWidth={1.75} /> Close
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto bg-[#2a2a2a] flex items-start justify-center py-6 px-3">
          {!supported && (
            <div className="bg-white p-8 max-w-md text-center">
              <div className="font-serif text-lg text-[#0D2B52] mb-2">Preview not supported</div>
              <p className="text-sm text-[#6B7280]">
                This document type cannot be previewed in the browser. Download it to view or highlight
                after upload of a supported format (images or PDFs).
              </p>
            </div>
          )}

          {supported && (
            <div
              ref={stageRef}
              className="relative select-none shadow-lg"
              style={{ cursor: "crosshair", touchAction: "none" }}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={() => {
                if (dragStart.current) {
                  dragStart.current = null;
                  setDragRect(null);
                }
              }}
              data-testid="viewer-stage"
            >
              {/* Image */}
              {isImage && (
                <img
                  src={fileUrl}
                  alt={doc.original_name}
                  draggable={false}
                  className="block max-h-[75vh] max-w-full h-auto w-auto"
                  onLoad={(e) => {
                    setPageDims({ w: e.currentTarget.offsetWidth, h: e.currentTarget.offsetHeight });
                    setNumPages(1);
                  }}
                  data-testid="viewer-image"
                />
              )}

              {/* PDF */}
              {isPdf && !pdfError && (
                <Document
                  file={fileUrl}
                  onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                  onLoadError={() => setPdfError(true)}
                  loading={<div className="p-10 text-white/70 text-sm">Loading PDF…</div>}
                >
                  <Page
                    pageNumber={pageNum}
                    width={Math.min(900, window.innerWidth - 40)}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    onRenderSuccess={(page) => {
                      const vp = page.getViewport({ scale: page.scale || 1 });
                      setPageDims({ w: vp.width, h: vp.height });
                    }}
                    data-testid={`viewer-pdf-page-${pageNum}`}
                  />
                </Document>
              )}

              {isPdf && pdfError && (
                <div className="bg-white p-8 max-w-md text-center">
                  <div className="font-serif text-lg text-[#0D2B52] mb-2">This PDF would not render in-browser</div>
                  <p className="text-sm text-[#6B7280]">
                    Some scans or encrypted PDFs need to be downloaded to view. You can still tag and reference
                    this document. Its highlights (if any) will still burn onto the exported bundle.
                  </p>
                </div>
              )}

              {/* Existing highlights */}
              {currentPageHighlights.map((h) => {
                const c = COLOR_MAP[h.color] || COLOR_MAP.yellow;
                return (
                  <div
                    key={h.id}
                    data-highlight="existing"
                    data-testid={`highlight-${h.id}`}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      openHighlight(h);
                    }}
                    className="absolute cursor-pointer"
                    style={{
                      left: `${h.x * 100}%`,
                      top: `${h.y * 100}%`,
                      width: `${h.w * 100}%`,
                      height: `${h.h * 100}%`,
                      backgroundColor: c.overlay,
                      outline: editing?.id === h.id ? "2px solid #0D2B52" : "none",
                    }}
                    title={h.comment || "(No note)"}
                  >
                    {h.comment && (
                      <span className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-[#0D2B52] text-white flex items-center justify-center pointer-events-none">
                        <MessageSquare size={9} strokeWidth={2} />
                      </span>
                    )}
                  </div>
                );
              })}

              {/* Live drag preview */}
              {dragRect && (
                <div
                  data-testid="viewer-drag-preview"
                  className="absolute pointer-events-none"
                  style={{
                    left: `${dragRect.x * 100}%`,
                    top: `${dragRect.y * 100}%`,
                    width: `${dragRect.w * 100}%`,
                    height: `${dragRect.h * 100}%`,
                    backgroundColor: COLOR_MAP[pickedColor].overlay,
                    outline: "1px dashed #0D2B52",
                  }}
                />
              )}
            </div>
          )}
        </div>

        {/* Footer / editor popover */}
        {editing ? (
          <div className="border-t border-[#E5E4DE] bg-white px-4 sm:px-6 py-3" data-testid="viewer-highlight-editor">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                {COLORS.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => saveEditing({ color: c.key })}
                    className={`w-6 h-6 rounded-full ${c.swatch} border border-[#E5E4DE] ring-offset-2 ${
                      editing.color === c.key ? `ring-2 ${c.ring}` : ""
                    }`}
                    aria-label={`Change to ${c.key}`}
                    data-testid={`highlight-editor-color-${c.key}`}
                  />
                ))}
              </div>
              <div className="flex-1 min-w-[220px]">
                <label className="label-mono text-[10px] text-[#6B7280] mb-1 block">Optional note</label>
                <input
                  type="text"
                  className="input-field text-sm"
                  placeholder="e.g. Rent-review clause. Refer to Annex 3."
                  value={commentDraft}
                  onChange={(e) => setCommentDraft(e.target.value)}
                  onBlur={() => {
                    if ((commentDraft || "") !== (editing.comment || "")) {
                      saveEditing({ comment: commentDraft || null });
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.currentTarget.blur();
                  }}
                  data-testid="highlight-editor-comment"
                  maxLength={280}
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={deleteHighlight}
                  className="btn-ghost inline-flex items-center gap-1.5 text-sm text-[#B35D4C] hover:text-[#8B3D2E]"
                  data-testid="highlight-editor-delete"
                >
                  <Trash2 size={14} strokeWidth={1.75} /> Delete
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  className="btn-secondary text-sm"
                  data-testid="highlight-editor-done"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="border-t border-[#E5E4DE] bg-white px-4 sm:px-6 py-2 flex items-center justify-between text-xs text-[#6B7280] flex-wrap gap-2">
            <span>
              Drag on the document to create a highlight. Click any existing highlight to add a note or delete it.
            </span>
            <span className="font-mono text-[11px]">
              {highlights.length} highlight{highlights.length === 1 ? "" : "s"} on this document
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
